'use client'

import { useLocalStorageObject } from '@shared/hooks'
import { setCookie } from '@shared/utils'
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'

import {
  DEFAULT_ONBOARDING_DRAFT,
  getOnboardingDraftForUser,
  getStartedOnboardingDraft,
  isOnboardingDraftOwnedByUser,
  ONBOARDING_COOKIE_KEY,
  ONBOARDING_STORAGE_KEY,
  type OnboardingBookingSlot,
  type OnboardingDraft,
  type OnboardingOwnerUserId,
  withOnboardingDraftOwner,
} from './draft'
import {
  EN_ONBOARDING_LEVEL_LABELS,
  JA_ONBOARDING_LEVEL_LABELS,
  KO_ONBOARDING_LEVEL_LABELS,
  ONBOARDING_LEVEL_LABEL,
} from './level-labels'
import {
  OnboardingBookingLanguage,
  InternalOnboardingStep,
  OnboardingClassStyle,
  OnboardingGoal,
  OnboardingPreferredTime,
} from './schema'

export {
  DEFAULT_ONBOARDING_DRAFT,
  getOnboardingDraftForUser,
  getStartedOnboardingDraft,
  isOnboardingDraftOwnedByUser,
  ONBOARDING_COOKIE_KEY,
  ONBOARDING_ELIGIBLE_COOKIE_KEY,
  ONBOARDING_ELIGIBLE_STORAGE_KEY,
  ONBOARDING_STORAGE_KEY,
  shouldClearOnboardingDraftOnPageLeave,
  withOnboardingDraftOwner,
} from './draft'
export type { OnboardingBookingSlot, OnboardingDraft, OnboardingOwnerUserId } from './draft'
export {
  createOnboardingDraftFromState,
  getBookedOnboardingState,
  getBookedLanguageTrialTypes,
  getHasBookedOnboardingState,
  getHasSkippedAdditionalTrialOnboardingDraft,
  getPrimaryTrialExperienceHomeState,
  getPurchaseableTrialLanguageTypes,
  getResumableAdditionalTrialLanguageTypes,
  getSkippedAdditionalTrialOnboardingState,
  shouldShowFirstTrialBookingToast,
  shouldShowTrialExperienceHomeCard,
  shouldShowLanguageTrialBanner,
  shouldShowOnboardingBookedCard,
  shouldShowOnboardingTrialCard,
  shouldShowReservedTrialLessonsSection,
} from './home-card'
export type {
  LanguageTrialType,
  OnboardingHomeState,
  TrialExperienceHomeCardType,
  TrialExperienceHomeCardPreparationItem,
  TrialExperienceHomeState,
} from './home-card'
export {
  canEnterJpTrialApplication,
  canEnterFirstTrialOnboardingStart,
  canRedirectHomeToFirstTrialOnboarding,
  canRestoreFirstTrialOnboardingDraft,
  canShowFirstTrialPopup,
  getOnboardingStateLookupStatus,
  hasCompanyCode,
  hasRegularLessonEntitlement,
  isFirstTrialOnboardingTarget,
  resolveJpTrialHomeCtaVariant,
} from './eligibility'

export type OnboardingDateItem = {
  day: string
  date: number
  fullDate: string
}

export type OnboardingTimeSlotStatus = 'open' | 'close' | 'penalty' | 'holding' | 'too-soon'

export type OnboardingTimeSlot = {
  time: string
  status: OnboardingTimeSlotStatus
  utcClassDateTime?: string
  originTime?: boolean
}

export type OnboardingTimeGroup = {
  groupName: string
  times: OnboardingTimeSlot[]
}

export const onboardingRouteMap: Record<InternalOnboardingStep, string> = {
  start: '/onboarding/start',
  language: '/onboarding/language',
  goal: '/onboarding/goal',
  experience: '/onboarding/experience',
  frequency: '/onboarding/frequency',
  'preferred-time': '/onboarding/preferred-time',
  'class-style': '/onboarding/class-style',
  loading: '/onboarding/loading',
  result: '/onboarding/result',
  booking: '/onboarding/booking',
  marketing: '/onboarding/marketing',
  complete: '/onboarding/complete',
}

export const QUESTION_STEPS = [
  'language',
  'goal',
  'experience',
  'frequency',
  'preferred-time',
  'class-style',
] as const satisfies readonly InternalOnboardingStep[]

export const QUESTION_PROGRESS: Record<(typeof QUESTION_STEPS)[number], number> = {
  language: 1,
  goal: 2,
  experience: 3,
  frequency: 4,
  'preferred-time': 5,
  'class-style': 6,
}

// 추가 체험 온보딩에서는 frequency/preferred-time 을 재사용하므로 3개 스텝만 노출한다.
export const REUSED_QUESTION_STEPS = [
  'goal',
  'experience',
  'class-style',
] as const satisfies readonly InternalOnboardingStep[]

export const REUSED_QUESTION_PROGRESS: Record<(typeof REUSED_QUESTION_STEPS)[number], number> = {
  goal: 1,
  experience: 2,
  'class-style': 3,
}

export type LevelOption = {
  label: string
}

export const LEVEL_OPTIONS_BY_LANGUAGE: Record<OnboardingBookingLanguage, LevelOption[]> = {
  en: EN_ONBOARDING_LEVEL_LABELS.map((label) => ({ label })),
  ja: JA_ONBOARDING_LEVEL_LABELS.map((label) => ({ label })),
  ko: KO_ONBOARDING_LEVEL_LABELS.map((label) => ({ label })),
}

export const GOAL_OPTIONS: { value: OnboardingGoal }[] = [
  { value: 'BUSINESS' },
  { value: 'TRAVEL' },
  { value: 'EXAM' },
  { value: 'GROWTH' },
  { value: 'MEDIA' },
  { value: 'FRIENDS' },
]

export const EXPERIENCE_OPTIONS: { value: 1 | 2 | 3 | 4 | 5 }[] = [
  { value: 1 },
  { value: 2 },
  { value: 3 },
  { value: 4 },
  { value: 5 },
]

export const PREFERRED_TIME_OPTIONS: { value: OnboardingPreferredTime }[] = [
  { value: 'WEEKDAY_MORNING' },
  { value: 'WEEKDAY_LUNCH' },
  { value: 'WEEKDAY_EVENING' },
  { value: 'WEEKEND_MORNING' },
  { value: 'WEEKEND_LUNCH' },
  { value: 'WEEKEND_EVENING' },
]

export const CLASS_STYLE_OPTIONS: { value: OnboardingClassStyle }[] = [
  { value: 'CURRICULUM' },
  { value: 'FREE_TALK' },
  { value: 'CORRECTION' },
  { value: 'RELAXED' },
]

export const getRecommendedLevel = (
  language: OnboardingBookingLanguage | null,
  experienceLevel: OnboardingDraft['experienceLevel'],
) => {
  if (!language || language === 'ko' || !experienceLevel) {
    return null
  }

  if (language === 'en') {
    if (experienceLevel <= 2) {
      return LEVEL_OPTIONS_BY_LANGUAGE.en[0]
    }
    if (experienceLevel === 3) {
      return LEVEL_OPTIONS_BY_LANGUAGE.en[1]
    }
    if (experienceLevel === 4) {
      return LEVEL_OPTIONS_BY_LANGUAGE.en[2]
    }
    return LEVEL_OPTIONS_BY_LANGUAGE.en[3]
  }

  if (experienceLevel === 1) {
    return LEVEL_OPTIONS_BY_LANGUAGE.ja[0]
  }
  if (experienceLevel === 2) {
    return LEVEL_OPTIONS_BY_LANGUAGE.ja[1]
  }
  if (experienceLevel <= 4) {
    return LEVEL_OPTIONS_BY_LANGUAGE.ja[2]
  }
  return LEVEL_OPTIONS_BY_LANGUAGE.ja[3]
}

export const getTrialClassroomParams = (language: OnboardingBookingLanguage | null, selectedLevel: string | null) => {
  if (!language || !selectedLevel) {
    return null
  }

  if (language === 'en') {
    switch (selectedLevel) {
      case ONBOARDING_LEVEL_LABEL.beginner:
        return { classLevel: 3, classWeek: 1, langType: 'EN' as const }
      case ONBOARDING_LEVEL_LABEL.upperBeginner:
      case ONBOARDING_LEVEL_LABEL.intermediate:
        return { classLevel: 4, classWeek: 1, langType: 'EN' as const }
      case ONBOARDING_LEVEL_LABEL.preAdvanced:
        return { classLevel: 5, classWeek: 10, langType: 'EN' as const }
      case ONBOARDING_LEVEL_LABEL.advanced:
        return { classLevel: 7, classWeek: 1, langType: 'EN' as const }
      default:
        return null
    }
  }

  const langType = language === 'ko' ? ('KR' as const) : ('JP' as const)

  switch (selectedLevel) {
    case ONBOARDING_LEVEL_LABEL.starter:
      return { classLevel: 1, classWeek: 1, langType }
    case ONBOARDING_LEVEL_LABEL.beginner:
      return { classLevel: 2, classWeek: 1, langType }
    case ONBOARDING_LEVEL_LABEL.intermediate:
      return { classLevel: 4, classWeek: 1, langType }
    case ONBOARDING_LEVEL_LABEL.advanced:
      return { classLevel: 8, classWeek: 1, langType }
    default:
      return null
  }
}

export const getTrialClassCourseId = (language: OnboardingBookingLanguage | null, selectedLevel: string | null) => {
  if (!language || language === 'ko' || !selectedLevel) {
    return null
  }

  if (language === 'en') {
    switch (selectedLevel) {
      case ONBOARDING_LEVEL_LABEL.beginner:
        return 6102
      case ONBOARDING_LEVEL_LABEL.upperBeginner:
      case ONBOARDING_LEVEL_LABEL.intermediate:
        return 6103
      case ONBOARDING_LEVEL_LABEL.preAdvanced:
        return 6104
      case ONBOARDING_LEVEL_LABEL.advanced:
        return 6105
      default:
        return null
    }
  }

  switch (selectedLevel) {
    case ONBOARDING_LEVEL_LABEL.starter:
      return 6106
    case ONBOARDING_LEVEL_LABEL.beginner:
      return 6108
    case ONBOARDING_LEVEL_LABEL.intermediate:
      return 6110
    case ONBOARDING_LEVEL_LABEL.advanced:
      return 6112
    default:
      return null
  }
}

export const getTrialLevelLabelFromClassroomParams = (
  language: OnboardingBookingLanguage | null,
  classLevel: number | null | undefined,
  classWeek: number | null | undefined,
) => {
  if (!language || !classLevel || !classWeek) {
    return null
  }

  if (language === 'en') {
    // JP 정규화된 MAIN 체험 코스는 11~14 레벨의 1주차를 사용한다.
    if (classWeek === 1) {
      const normalizedLevel = EN_ONBOARDING_LEVEL_LABELS[classLevel - 11]
      if (normalizedLevel) {
        return normalizedLevel
      }
    }
    if (classLevel === 3 && classWeek === 1) {
      return ONBOARDING_LEVEL_LABEL.beginner
    }
    if (classLevel === 4 && classWeek === 1) {
      return ONBOARDING_LEVEL_LABEL.intermediate
    }
    if (classLevel === 5 && classWeek === 10) {
      return ONBOARDING_LEVEL_LABEL.preAdvanced
    }
    if (classLevel === 7 && classWeek === 1) {
      return ONBOARDING_LEVEL_LABEL.advanced
    }
    return null
  }

  if (language === 'ko') {
    if (classLevel === 11 && classWeek === 1) {
      return ONBOARDING_LEVEL_LABEL.starter
    }
    if (classLevel === 12 && classWeek === 1) {
      return ONBOARDING_LEVEL_LABEL.beginner
    }
    if (classLevel === 13 && classWeek === 1) {
      return ONBOARDING_LEVEL_LABEL.intermediate
    }
    if (classLevel === 14 && classWeek === 1) {
      return ONBOARDING_LEVEL_LABEL.advanced
    }
  }

  if (classLevel === 1 && classWeek === 1) {
    return ONBOARDING_LEVEL_LABEL.starter
  }
  if (classLevel === 2 && classWeek === 1) {
    return ONBOARDING_LEVEL_LABEL.beginner
  }
  if (classLevel === 4 && classWeek === 1) {
    return ONBOARDING_LEVEL_LABEL.intermediate
  }
  if (classLevel === 8 && classWeek === 1) {
    return ONBOARDING_LEVEL_LABEL.advanced
  }

  return null
}

export const getEarliestAvailableStep = (draft: OnboardingDraft): InternalOnboardingStep => {
  if (!draft.language) {
    return 'language'
  }
  if (draft.goals.length === 0) {
    return 'goal'
  }
  if (!draft.experienceLevel) {
    return 'experience'
  }
  if (!draft.frequency) {
    return 'frequency'
  }
  if (draft.preferredTime.length === 0) {
    return 'preferred-time'
  }
  if (draft.classStyle.length === 0) {
    return 'class-style'
  }
  if (!draft.selectedSlot && !draft.trialSkipped) {
    return 'result'
  }
  if (!draft.selectedSlot && draft.trialSkipped) {
    return 'complete'
  }
  return 'complete'
}

export const getGuardRoute = (step: InternalOnboardingStep, draft: OnboardingDraft) => {
  if (step === 'marketing') {
    return draft.trialBooked ? onboardingRouteMap.complete : '/onboarding/booking?from=home'
  }
  if (step !== 'start' && draft.status === 'idle') {
    return onboardingRouteMap.start
  }

  switch (step) {
    case 'start':
      return null
    case 'language':
      return draft.status === 'in-progress' ? null : onboardingRouteMap.start
    case 'goal':
      return draft.language ? null : onboardingRouteMap.language
    case 'experience':
      return draft.goals.length > 0 ? null : onboardingRouteMap.goal
    case 'frequency':
      return draft.experienceLevel ? null : onboardingRouteMap.experience
    case 'preferred-time':
      return draft.frequency ? null : onboardingRouteMap.frequency
    case 'class-style':
      return draft.preferredTime.length > 0 ? null : onboardingRouteMap['preferred-time']
    case 'loading':
    case 'result':
      return draft.classStyle.length > 0 ? null : onboardingRouteMap['class-style']
    case 'booking':
      return draft.classStyle.length > 0 ? null : onboardingRouteMap.result
    case 'complete':
      return draft.selectedSlot || draft.trialSkipped ? null : onboardingRouteMap.result
    default:
      return onboardingRouteMap.start
  }
}

type OnboardingContextValue = {
  draft: OnboardingDraft
  setDraft: (updater: OnboardingDraft | ((current: OnboardingDraft) => OnboardingDraft)) => void
  resetDraft: () => void
  isReady: boolean
  ownerUserId: OnboardingOwnerUserId
}

const OnboardingContext = createContext<OnboardingContextValue | null>(null)

export const useOnboardingStorage = (
  initialValue: OnboardingDraft = DEFAULT_ONBOARDING_DRAFT,
  ownerUserId?: OnboardingOwnerUserId,
) => {
  const initialDraftForUser = getOnboardingDraftForUser(initialValue, ownerUserId)
  const [draft, setDraftState, resetDraftState] = useLocalStorageObject<OnboardingDraft>(
    ONBOARDING_STORAGE_KEY,
    initialDraftForUser,
  )
  const [isReady, setIsReady] = useState(false)

  const storedDraft = draft ?? initialDraftForUser
  const normalizedDraft = getOnboardingDraftForUser(storedDraft, ownerUserId)

  useEffect(() => {
    setCookie(ONBOARDING_COOKIE_KEY, JSON.stringify(normalizedDraft), 60 * 60 * 24 * 30)
  }, [normalizedDraft])

  useEffect(() => {
    if (isOnboardingDraftOwnedByUser(storedDraft, ownerUserId)) {
      return
    }

    setDraftState(DEFAULT_ONBOARDING_DRAFT)
  }, [ownerUserId, setDraftState, storedDraft])

  useEffect(() => {
    setIsReady(true)
  }, [])

  const setDraft = useCallback(
    (updater: OnboardingDraft | ((current: OnboardingDraft) => OnboardingDraft)) => {
      setDraftState((current) => {
        const nextCurrent = getOnboardingDraftForUser(current ?? initialValue, ownerUserId)

        if (typeof updater === 'function') {
          return withOnboardingDraftOwner(updater(nextCurrent), ownerUserId)
        }

        return withOnboardingDraftOwner(updater, ownerUserId)
      })
    },
    [initialValue, ownerUserId, setDraftState],
  )

  const resetDraft = useCallback(() => {
    resetDraftState()
    setCookie(ONBOARDING_COOKIE_KEY, JSON.stringify(DEFAULT_ONBOARDING_DRAFT), 60 * 60 * 24 * 30)
  }, [resetDraftState])

  return [normalizedDraft, setDraft, resetDraft, isReady] as const
}

export const OnboardingProvider = ({
  children,
  initialDraft,
  ownerUserId,
}: {
  children: React.ReactNode
  initialDraft?: OnboardingDraft
  ownerUserId?: OnboardingOwnerUserId
}) => {
  // 서버 컴포넌트가 쿠키에서 읽어온 initialDraft 를 넘기면 SSR 시점부터 draft 가 확정된다.
  // (이게 없으면 DEFAULT(idle) 로 첫 렌더 후 클라이언트 hydration 에서 뒤늦게 localStorage 로 교체되는데,
  //  useEffect 가 그 사이 DEFAULT 기준으로 분기를 마쳐버리는 타이밍 이슈가 생긴다.)
  const [draft, setDraft, resetDraft, isReady] = useOnboardingStorage(initialDraft, ownerUserId)

  const value = useMemo(
    () => ({
      draft,
      setDraft,
      resetDraft,
      isReady,
      ownerUserId,
    }),
    [draft, isReady, ownerUserId, resetDraft, setDraft],
  )

  return <OnboardingContext.Provider value={value}>{children}</OnboardingContext.Provider>
}

export const useOnboarding = () => {
  const context = useContext(OnboardingContext)

  if (!context) {
    throw new Error('useOnboarding must be used within OnboardingProvider')
  }

  return context
}
